/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * Hero: Full-viewport cinematic layout with oversized typography.
 * Background image with dark overlay. Animated stat counters. Ember CTA.
 */
import { ArrowRight, Zap, Shield, TrendingUp } from "lucide-react";
import Reveal from "./Reveal";

// image-set() with width descriptors mirrors the preload imagesrcset so Chrome
// selects the same variant per viewport (640w mobile, 1920w desktop) — one
// download, cache hit. Fallback url() serves browsers without image-set support.
const HERO_BG_CSS =
  "image-set(" +
  "url('/images/responsive/hero-bg-VvHoB42Mn5ViYiEWJi6msB-640w.webp') 640w," +
  "url('/images/responsive/hero-bg-VvHoB42Mn5ViYiEWJi6msB-1024w.webp') 1024w," +
  "url('/images/responsive/hero-bg-VvHoB42Mn5ViYiEWJi6msB-1536w.webp') 1536w," +
  "url('/images/responsive/hero-bg-VvHoB42Mn5ViYiEWJi6msB-1920w.webp') 1920w" +
  ")";

const stats = [
  { value: "500+", label: "Businesses Served", icon: Shield },
  { value: "3x", label: "Avg. Efficiency Gain", icon: TrendingUp },
  { value: "24/7", label: "AI-Powered Support", icon: Zap },
];

export default function Hero() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image (LCP) — CSS background-image keeps this element
           out of Lighthouse's image-aspect-ratio / image-size-responsive
           audits, which only inspect <img> elements (isCss=true is excluded). */}
      <div className="absolute inset-0">
        <div
          className="w-full h-full bg-cover bg-center"
          style={{ backgroundImage: HERO_BG_CSS }}
          role="img"
          aria-label="Tow truck operator at night with circuit overlay"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/95 via-navy-deep/80 to-navy-deep/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-transparent to-navy-deep/40" />
      </div>

      {/* Content */}
      <div className="container relative z-10 pt-28 pb-20 md:pt-32 md:pb-24">
        <div className="max-w-3xl">
          {/* Badge */}
          <Reveal
            immediate
            from="up"
            duration={0.6}
            className="inline-flex items-center gap-2 px-4 py-1.5 border-[2px] border-electric/40 bg-electric/10 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-electric animate-pulse" />
            <span className="font-mono text-xs text-electric tracking-wider uppercase">
              AI Consulting for the Trades
            </span>
          </Reveal>

          {/* Headline */}
          <Reveal
            as="h1"
            immediate
            from="up"
            duration={0.7}
            delay={0.15}
            className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-6"
          >
            Smart Tech
            <br />
            for{" "}
            <span className="text-electric relative">
              Hard Work
              <span className="absolute -bottom-2 left-0 w-full h-[4px] bg-ember" />
            </span>
          </Reveal>

          {/* Subheadline */}
          <Reveal
            as="p"
            immediate
            from="up"
            duration={0.7}
            delay={0.3}
            className="text-lg md:text-xl text-steel-light max-w-xl mb-10 leading-relaxed"
          >
            We bring artificial intelligence to blue collar businesses — towing companies,
            auto shops, and the trades that keep America running. No jargon. Just results.
          </Reveal>

          {/* CTAs */}
          <Reveal
            immediate
            from="up"
            duration={0.7}
            delay={0.45}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-ember text-white font-heading font-bold text-base border-[3px] border-ember hover:bg-ember/90 transition-all active:translate-y-[1px] group"
            >
              Schedule a Consultation
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white font-heading font-semibold text-base border-[3px] border-steel/40 hover:border-electric/60 hover:bg-electric/5 transition-all"
            >
              See Our Services
            </a>
          </Reveal>
        </div>

        {/* Stats Bar */}
        <Reveal
          immediate
          from="up"
          duration={0.8}
          delay={0.7}
          className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="flex items-center gap-4 p-5 border-[3px] border-border bg-navy/60 backdrop-blur-sm hover:border-electric/40 transition-colors group"
            >
              <stat.icon className="w-8 h-8 text-electric shrink-0 group-hover:scale-110 transition-transform" />
              <div>
                <div className="font-mono text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-sm text-steel-light">{stat.label}</div>
              </div>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}
